import { useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { LinkCreationToolProps, LinkStatus } from '../../../types/infrastructure-map'
import {
  ChildrenSelection,
  LinkTypeSelector,
  OpenQuestionField,
  ParentSelection,
  ProgressSteps,
  ToolFooter,
  ToolHeader,
} from './link-creation-tool/link-creation-tool.parts'
import { deriveCurrentStep } from './link-creation-tool/link-creation-tool.helpers'

type LinkCreationToolComponentProps = Omit<LinkCreationToolProps, 'caseId' | 'onSelectParent'>

export function LinkCreationTool(props: LinkCreationToolComponentProps): ReactElement | null {
  const { isActive, parentEndpointId, childEndpointIds, onToggleChild, onComplete, onCancel } =
    props
  const { t } = useTranslation('cases')
  const [selectedLinkType, setSelectedLinkType] = useState<LinkStatus>('confirmed')
  const [openQuestionText, setOpenQuestionText] = useState('')

  useEffect(() => {
    if (!isActive) {
      setSelectedLinkType('confirmed')
      setOpenQuestionText('')
    }
  }, [isActive])

  if (!isActive) return null

  const canComplete = !!parentEndpointId && childEndpointIds.length > 0
  const currentStep = deriveCurrentStep(parentEndpointId, childEndpointIds.length)

  const handleComplete = (): void => {
    if (!canComplete) return
    onComplete(
      selectedLinkType,
      selectedLinkType === 'open-question' ? openQuestionText : undefined
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/20 pt-20">
      <div className="mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-slate-800">
        <ToolHeader onCancel={onCancel} t={t} />
        <ProgressSteps currentStep={currentStep} t={t} />
        <div className="space-y-4 px-6 py-4">
          <ParentSelection parentEndpointId={parentEndpointId} t={t} />
          <ChildrenSelection
            parentEndpointId={parentEndpointId}
            childEndpointIds={childEndpointIds}
            onToggleChild={onToggleChild}
            t={t}
          />
          <LinkTypeSelector
            selectedLinkType={selectedLinkType}
            setSelectedLinkType={setSelectedLinkType}
            t={t}
          />
          {selectedLinkType === 'open-question' && (
            <OpenQuestionField
              openQuestionText={openQuestionText}
              setOpenQuestionText={setOpenQuestionText}
              t={t}
            />
          )}
        </div>
        <ToolFooter
          canComplete={canComplete}
          childCount={childEndpointIds.length}
          onCancel={onCancel}
          onComplete={handleComplete}
          t={t}
        />
      </div>
    </div>
  )
}

export default LinkCreationTool
